import { COMMUNICATOR_ENFORCE_PCMU_CODEC } from 'Constants/featureFlags';
import {
  ACTIVE_CALL,
  SELECTED_MICROPHONE,
  SELECTED_SPEAKER,
} from 'Constants/localstorage';

import { EventEmitter } from 'events';
import { PhoneNumberFormat } from 'google-libphonenumber';
import * as localforage from 'localforage';
import { isString } from 'lodash';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { IPromiseBasedObservable, fromPromise } from 'mobx-utils';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { useNotificationStore } from 'Modules/notification/store';
import { SoftphoneCoordinator } from 'Modules/softphone/utils/softphoneCoordinator';
import { useCallSurveyStore } from 'Modules/survey/store';
import moment from 'moment-timezone';
import { VoipMonitorService } from 'Services/voipMonitorService';
import {
  VoipMonitorEvent,
  CallStats,
  CollectedStats,
  ExtendedSession,
  VoipCallQualitySnapshot,
} from 'Services/voipMonitorService/index.types';
import { SdpService } from 'Services/voipSdpService';
import {
  Invitation,
  InvitationAcceptOptions,
  Inviter,
  InviterInviteOptions,
  Session,
  SessionDescriptionHandlerModifier,
  SessionState,
  URI,
  UserAgent,
  Web,
} from 'sip.js';
import { PhoneStore } from 'Stores/PhoneStore';
import { RootStore } from 'Stores/RootStore';
import { isNullOrUndefined } from 'util';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { getLogger, Logger } from 'Utils/logger';
import { bugsnagClient } from 'Utils/logUtils';
import { logError } from 'Utils/phoneLogUtils';
import phoneUtil from 'Utils/phoneUtil';
import { stopMediaStream } from 'Utils/streamUtils';
import { CALL_PERSIST, TIMEZONE_IDENTIFIER } from '../constants/env';

import { VoipTerminationService } from '../services/voipTerminationService';
import {
  TerminationReason,
  ExtractedTerminationData,
} from '../services/voipTerminationService/index.types';

/**
 *
 * Events
 * - `terminated`: `() => void`
 */
export class PhoneCallModel extends EventEmitter {
  public rootStore: RootStore;
  // call survey
  public callStartTime: string = null;
  public callEndTime: string = null;
  // log collector
  private callStartRequestTime: number = null;
  private callAnswerTime: number = null;

  private logger: Logger;

  private voipMonitorService: VoipMonitorService;

  private isUserInitiatedTermination = false;
  private terminationContext: {
    reason?: TerminationReason;
    statusCode?: number;
    reasonPhrase?: string;
    sipjsCause?: string;
    additionalDetails?: Record<string, unknown>;
  } = {};

  private analytics: any;

  // Add these private properties to store listener references
  private voipMonitorListeners: {
    monitoringEnded?: (
      callId: string,
      stats: CollectedStats | CallStats
    ) => void;
    callQualitySnapshot?: (snapshot: VoipCallQualitySnapshot) => void;
    error?: (error: Error) => void;
  } = {};

  constructor(
    rootStore: RootStore,
    phoneStore: PhoneStore,
    loggedInCallerId: any,
    loggedInAccountId: number,
    loggedInPersonId: number,
    session?: Session,
    phoneUri?: URI,
    person?: IPromiseBasedObservable<ContactModelV2>,
    contact?: ContactModelV2,
    remoteAudio?: HTMLAudioElement,
    ringTone?: HTMLAudioElement,
    incomingTone?: HTMLAudioElement,
    secondIncomingTone?: HTMLAudioElement,
    lostConnectionTone?: HTMLAudioElement,
    localCallDisconnectedTone?: HTMLAudioElement,
    isWarmTransfer?: boolean,
    isMerge?: boolean,
    conferenceId?: string,
    handleAccept?: any,
    callBack?: any,
    callStartTime?: string,
    callEndTime?: string,
    otherContacts?: ContactModelV2[],
    phoneNumber?: string
  ) {
    super();
    makeObservable(this);
    this.phoneStore = phoneStore;
    this.rootStore = rootStore;

    this.setLoggedInUserCallerId(loggedInCallerId);
    this.setLoggedInAccountId(loggedInAccountId);
    this.setLoggedInPersonId(loggedInPersonId);
    this.setPhoneURI(phoneUri);
    this.setPerson(person);
    this.setContact(contact);
    this.setOtherContacts(otherContacts);

    // Definir phoneNumber antes de configurar sessão
    if (phoneNumber) {
      this.setPhoneNumber(phoneNumber);
    }

    // Agora configurar a sessão com todos os dados disponíveis
    this.configSession(session);

    // Resto da configuração...
    this.setIsCallConnecting(true);
    this.remoteAudio = remoteAudio;
    this.ringtone = ringTone;
    this.incomingTone = incomingTone;
    this.secondIncomingTone = secondIncomingTone;
    this.lostConnectionTone = lostConnectionTone;
    this.localCallDisconnectedTone = localCallDisconnectedTone;
    this.setWarmTransfer(isWarmTransfer);
    this.setMergeTransfer(isMerge);
    this.setConferenceId(conferenceId);
    this.handleAccept = handleAccept;
    this.callBack = callBack;
    this.callStartTime = callStartTime;
    this.callEndTime = callEndTime;
    this.logger = getLogger();
    this.voipMonitorService = VoipMonitorService.getInstance({
      sendFullCallStats: this.getFullCallStatsFlag(),
      enableAlertAdapter: true,
    });
    this.setupVoipMonitorListeners();
  }

  /** Configures the `Session` and assigns it to `this.session` */
  public configSession = (s: Session) => {
    if (s !== null) {
      const callId = s.id;

      // Criar as inviteOptions AQUI, no momento do invite
      const inviteOptions: InviterInviteOptions = {
        requestDelegate: {
          onAccept: (resp) => {
            this.handleOnAccept();
            if (this.callBack) {
              this.callBack(this.phoneStore.phoneCalls);
            }
          },
          onReject: (reason) => {
            if (this.isCallCanceled && reason.message.statusCode === 487) {
              /**
               * SIP status 487 (Request Terminated) indicates:
               * - Call was cancelled before being established
               * - User manually rejected/cancelled a ringing call
               * - SIP client cancelled request before final response
               * This is normal behavior, not an error condition
               */
              return;
            }

            if (
              reason.message.statusCode >= 400 &&
              reason.message.statusCode !== 487
            ) {
              /**
               * Log error for all errors except 487
               */
              this.logCallError(callId, reason);
            }

            if (reason.message.statusCode === 407) {
              /**
               * 407 Proxy Authentication Required indicates SIP registration issue.
               */
              useNotificationStore.getState().addNotify({
                type: 'error',
                title: 'Call Terminated',
                message:
                  'The call was terminated due to an issue with SIP registration. Please check your connection and try again.',
                dismissible: true,
                autoDismiss: false,
              });
            }
          },

          onProgress: (response) => {
            /**
             * For outbound calls, log delay between call start and ringing
             */
            if (
              response.message.statusCode === 180 &&
              this.callStartRequestTime
            ) {
              const delay = Date.now() - this.callStartRequestTime;
              this.logCallDelay(
                delay,
                callId,
                'Outbound (Call -> TRYING -> RINGING)'
              );
            }

            this.startOfCall = moment.utc().format().toString();
            const activeConference =
              !isNullOrUndefined(this.rootStore?.conversationStore) &&
              this.rootStore.uiStore.IsOnVideoConference;
            if (response.message.statusCode === 183 && response.message.body) {
              if (
                this.phoneStore?.phoneCalls?.length <= 1 &&
                !activeConference
              ) {
                void this.pauseAndResetPlayPromise(
                  this.ringtone,
                  this.ringtonePlayPromise
                );
                this.setupRemoteMedia();
              } else if (activeConference) {
                void this.pauseAndResetPlayPromise(
                  this.ringtone,
                  this.ringtonePlayPromise
                );
                this.ringtone.volume = 0;
              } else {
                this.ringtonePlayPromise = this.ringtone?.play();
                this.ringtone.volume = 0.05;
              }
            } else if (response.message.statusCode === 180) {
              this.ringtonePlayPromise = this.ringtone?.play();
              this.ringtone.volume = 1.0;
            } else {
              void this.pauseAndResetPlayPromise(
                this.ringtone,
                this.ringtonePlayPromise
              );
            }
          },
        },
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: false,
          },
        },
        sessionDescriptionHandlerModifiers:
          this.getSessionDescriptionHandlerModifiers(),
      };

      /**
       * Listener for SIP session state changes.
       * Handles different states of a call:
       * - Established: Call is connected and audio is flowing
       * - Terminated: Call has ended (either normally or due to error)
       *
       * @param {SessionState} newState - The new state of the SIP session
       * @see {@link https://sipjs.com/api/0.15.0/session/#state}
       */
      s.stateChange.addListener((newState: SessionState) => {
        switch (newState) {
          case SessionState.Terminated:
            // Set call end time for survey
            this.callEndTime = moment.utc().format().toString();
            useCallSurveyStore(this.loggedInPersonId.toString())
              .getState()
              .setCallEndTime(this.callEndTime);

            this.extractTerminationDetails(s);
            this.logCallTermination();

            // Optionally persist call by skipping these steps if the flag is true and call is incoming, for testing purposes only.
            if (!CALL_PERSIST || !this.isIncomingCall) {
              if (this.isWarmTransfer) {
                this.phoneStore.warmTransferOld.setTransferMode(true);
              }
              void localforage.removeItem(ACTIVE_CALL);
              this.clearKeyPress();
              this.setShowDialPad(false);
              this.muteIconState('microphone');
              this.setIsCallMuted(false);
              this.onHoldIconState('pause circle outline');
              this.setIsCallOnHold(false);
              this.setIsMediaConnecting(false);
              this.setIsCallConnected(false);
              this.setIsCallConnecting(false);
              this.setIncomingCallValue(false);
              this.setIsMediaConnected(false);
              void this.pauseAndResetPlayPromise(
                this.ringtone,
                this.ringtonePlayPromise
              );
              void this.pauseAndResetPlayPromise(
                this.incomingTone,
                this.incomingTonePlayPromise
              );
              void this.pauseAndResetPlayPromise(
                this.secondIncomingTone,
                this.secondIncomingTonePlayPromise
              );
              this.clearPersonAndContact();
              this.resetDisplay();
              this.phoneStore.removeFromPhoneCallsArray(s);
              this.session = null;
              this.emit('terminated'); //TODO: Is this being listened to anywhere?!
            } else {
              console.warn(
                `PhoneCallModel session termination skipping cleanup due to CALL_PERSIST! You must refresh the page to remove the incoming call!`
              );
              // Clean up ringtone anyways so it doesn't keep playing while testing
              void this.pauseAndResetPlayPromise(
                this.ringtone,
                this.ringtonePlayPromise
              );
              void this.pauseAndResetPlayPromise(
                this.incomingTone,
                this.incomingTonePlayPromise
              );
              void this.pauseAndResetPlayPromise(
                this.secondIncomingTone,
                this.secondIncomingTonePlayPromise
              );
            }

            if (!CALL_PERSIST) {
              runInAction(() =>
                this.phoneStore.incomingPhoneCalls.remove(this)
              );
            } else {
              console.warn(
                `CALL_PERSIST enabled, call with URI ${this.callUri} will NOT be removed incoming calls! You must refresh the page to remove the incoming call!`
              );
            }

            // Increment call count for survey
            useCallSurveyStore(this.loggedInPersonId.toString())
              .getState()
              .incrementCallCountByCallId({
                callId,
                isMerged: this.isMerge,
              });

            this.voipMonitorService.stopMonitoring();
            this.cleanupVoipMonitorListeners(); // Add this line
            break;
          case SessionState.Established:
            if (this.phoneStore.phoneCalls.length > 1) {
              this.phoneStore.setOtherSessionsOnHold(s);
              this.phoneStore.makeCallActive(s);
              this.isCallOnHold = false;
            }

            this.handleOnAccept();

            // Start monitoring the call (WebRTC Metrics)
            this.voipMonitorService.startMonitoring({
              personId: this.loggedInPersonId.toString(),
              callId,
              session: s as ExtendedSession,
            });

            // Set call start time and end time for survey
            this.callStartTime = moment.utc().format().toString();
            useCallSurveyStore(this.loggedInPersonId.toString())
              .getState()
              .setCallStartTime(this.callStartTime);

            // For inbound calls, log delay between answer and established
            if (this.callAnswerTime) {
              const delay = Date.now() - this.callAnswerTime;
              this.logCallDelay(
                delay,
                callId,
                'Inbound (INCOMING_ANSWERED -> ESTABLISHED)'
              );
            }
            break;
        }
      });

      this.session = s;

      if (this.session instanceof Inviter && this.session.invite) {
        // Log Collector - Call Start Request Time
        this.callStartRequestTime = Date.now();

        // Send Invite Request
        void this.session.invite(inviteOptions);
      }
    } else {
      logError(null, 'There is NO SESSION', 'PhoneCallModel', 'configSession');
    }
  };

  public ringtonePlayPromise: Promise<void> = null;
  public incomingTonePlayPromise: Promise<void> = null;
  public secondIncomingTonePlayPromise: Promise<void> = null;
  public handleAccept: any;
  public callBack: any;
  public localMediaPlayPromise: Promise<void> = null;
  public remoteMediaPlayPromise: Promise<void> = null;
  /**
   * If available, awaits the `Promise` returned from `HTMLAudioElement.play()`, then pauses and resets the `audioElem`.
   *
   * Optionally, a `newMediaStream` can be provided to replace the `srcObject` of the `HTMLAudioElement`
   *
   * Returns a Promise containing `true` if the `Promise` was available and the `audioElem` was paused, `false` if it was not available and the `audioElem` was paused, or `null` if both were null/undefined.
   */
  public pauseAndResetPlayPromise = (
    audioElem: HTMLAudioElement,
    playPromise: Promise<void>,
    newMediaStream: MediaStream = null
  ): Promise<boolean | null> => {
    if (!isNullOrUndefined(audioElem)) {
      if (playPromise !== null) {
        return playPromise
          .then(() => {
            audioElem.pause();
            if (newMediaStream !== null) {
              audioElem.srcObject = newMediaStream;
            }
            audioElem.currentTime = 0;
            return true;
          })
          .catch((error) => {
            if (
              error?.name === 'NotAllowedError' ||
              error?.name === 'NotSupportedError'
            ) {
              this.logger.error(
                `Audio playback error: ${error.name}; Message: ${
                  error.message || 'No error message'
                }; Element: ${
                  audioElem?.title || 'unnamed'
                }; Timestamp: ${new Date().toISOString()}`
              );
            }

            return false;
          });
      }
      // If promise isn't available, synchronously reset
      audioElem.pause();
      if (newMediaStream !== null) {
        audioElem.srcObject = newMediaStream;
      }
      audioElem.currentTime = 0;
      return Promise.resolve(false);
    }

    return Promise.resolve(null);
  };

  public closeCurrentStreams = () => {
    const noCalls = this.phoneStore.phoneCalls?.length === 0;
    const allTones = [
      this?.ringtone?.srcObject,
      this?.incomingTone?.srcObject,
      this?.secondIncomingTone?.srcObject,
    ].filter(Boolean);
    //@ts-ignore
    noCalls && window.localStream && allTones.push(window.localStream);
    allTones.length > 0 &&
      allTones.forEach((mediaStream: MediaStream) =>
        stopMediaStream(mediaStream)
      );
  };

  private getFullCallStatsFlag(): boolean {
    const config = this.rootStore.configStore.signedInPersonConfig;
    return (
      config.state === 'fulfilled' &&
      !!config.value.data.feature?.sendFullCallStats?.enabled
    );
  }

  /**
   * Log Collector - Call Start Request Time
   */
  private logCallDelay(delay: number, callId: string, direction: string) {
    const message = `Call connection delay: ${delay}; CallId: ${callId}; Direction: ${direction}; Timestamp: ${new Date().toISOString()}`;
    this.logger.info(message);
  }

  /**
   * Log Collector - Call Connection Error
   */
  private logCallError(callId: string, reason: any) {
    const message = `Call connection error; CallId: ${callId}; Error: ${reason.message.reasonPhrase}`;
    this.logger.error(message);
  }

  /**
   * Sets up event listeners for call metrics monitoring
   * Used to track call quality and connection statistics
   * @private
   */
  private setupVoipMonitorListeners(): void {
    // Create listener functions and store references
    this.voipMonitorListeners.monitoringEnded = (
      callId: string,
      stats: CollectedStats | CallStats
    ) => {
      console.log(`[VoipMonitor]: Stats collected for call: ${callId}`, stats);

      this.logger.info(
        `[VoipMonitor]: Stats collected for call: ${callId}`,
        stats
      );
    };

    this.voipMonitorListeners.callQualitySnapshot = (
      snapshot: VoipCallQualitySnapshot
    ) => {
      console.log(
        `[VoipMonitor]: Call quality snapshot: ${snapshot.callId} for`,
        snapshot
      );

      this.logger.info(
        `[VoipMonitor]: Call quality snapshot: ${snapshot.callId}`,
        snapshot
      );
    };

    this.voipMonitorListeners.error = (error: Error) => {
      this.logger.error(
        `[VoipMonitor]: Error collecting metrics: ${error.message}`
      );
    };

    // Add the listeners
    this.voipMonitorService.on(
      VoipMonitorEvent.MONITORING_ENDED,
      this.voipMonitorListeners.monitoringEnded
    );

    this.voipMonitorService.on(
      VoipMonitorEvent.CALL_QUALITY_SNAPSHOT,
      this.voipMonitorListeners.callQualitySnapshot
    );

    this.voipMonitorService.on(
      VoipMonitorEvent.ERROR,
      this.voipMonitorListeners.error
    );
  }

  /**
   * Removes event listeners for call metrics monitoring
   * Called when the call terminates to prevent memory leaks
   * @private
   */
  private cleanupVoipMonitorListeners(): void {
    if (this.voipMonitorListeners.monitoringEnded) {
      this.voipMonitorService.removeListener(
        VoipMonitorEvent.MONITORING_ENDED,
        this.voipMonitorListeners.monitoringEnded
      );
    }

    if (this.voipMonitorListeners.callQualitySnapshot) {
      this.voipMonitorService.removeListener(
        VoipMonitorEvent.CALL_QUALITY_SNAPSHOT,
        this.voipMonitorListeners.callQualitySnapshot
      );
    }

    if (this.voipMonitorListeners.error) {
      this.voipMonitorService.removeListener(
        VoipMonitorEvent.ERROR,
        this.voipMonitorListeners.error
      );
    }

    // Clear the references
    this.voipMonitorListeners = {};
  }

  private extractTerminationDetails(session: Session): void {
    try {
      const extractedData: ExtractedTerminationData =
        VoipTerminationService.getInstance().extractTerminationDetails(
          session,
          this.isUserInitiatedTermination
        );

      if (extractedData.reason) {
        this.terminationContext.reason = extractedData.reason;
      }

      if (extractedData.statusCode !== undefined) {
        this.terminationContext.statusCode = extractedData.statusCode;
      }

      if (extractedData.reasonPhrase !== undefined) {
        this.terminationContext.reasonPhrase = extractedData.reasonPhrase;
      }

      if (extractedData.sipjsCause !== undefined) {
        this.terminationContext.sipjsCause = extractedData.sipjsCause;
      }

      if (extractedData.additionalDetails) {
        this.terminationContext.additionalDetails =
          extractedData.additionalDetails;
      }
    } catch (error) {
      this.logger?.error(
        'Failed to extract session termination details:',
        error
      );
    }
  }

  private logCallTermination(): void {
    if (!this.session) {
      return;
    }

    const direction = this.isIncomingCall ? 'Incoming' : 'Outgoing';
    const caller =
      direction === 'Outgoing'
        ? this.currentUserCallerId || ''
        : this.phoneNumber || '';
    const callee =
      direction === 'Incoming'
        ? this.phoneNumber || ''
        : this.currentUserCallerId || '';

    const terminationReason =
      VoipTerminationService.getInstance().mapTerminationReason(
        this.terminationContext?.statusCode,
        this.terminationContext?.reasonPhrase,
        this.session?.state,
        this.isUserInitiatedTermination,
        this.phoneStore.isCallTimeout
      );

    const terminationEvent = VoipTerminationService.getInstance().createEvent({
      callId: this.session.id,
      personId: this.loggedInPersonId?.toString() || '',
      caller,
      callee,
      terminationReason,
      callStartTime: this.callStartTime,
      platform: 'web',
      sipStatusCode: this.terminationContext?.statusCode,
      sipReasonPhrase: this.terminationContext?.reasonPhrase,
      sipjsCause: this.terminationContext?.sipjsCause,
      additionalTerminationDetails: {
        isWebRTCConnected: this.phoneStore.isWebRTCConnected,
        sessionState: this.session?.state,
        ...(this.terminationContext?.additionalDetails &&
        typeof this.terminationContext.additionalDetails === 'object'
          ? this.terminationContext.additionalDetails
          : {}),
      },
    });

    this.logger?.info?.('CallTerminationEvent', terminationEvent);

    this.resetTerminationContext();
  }

  private resetTerminationContext(): void {
    this.isUserInitiatedTermination = false;
    this.terminationContext = {};
  }

  // * ---------- OBSERVABLES ---------- *

  @observable
  public isCallConnected = false;

  @observable
  public phoneNumber = '';

  @observable
  public callUri: string = null;

  @observable
  public conferenceId = '';

  @observable
  public currentLoginPhoneNumber = '000-000-0000';

  @observable
  public currentUserCallerId: string;

  @observable
  public isCallConnecting: boolean;

  @observable
  public timerDisplay = '00:00:00';

  @observable
  public startTime: number;

  @observable
  public elapsedTime: number;

  @observable
  public isMediaConnecting: boolean;

  @observable
  public showDialPad = false;

  @observable
  public loggedInUserAccountId: number;

  @observable
  public isMediaConnected: boolean;

  @observable
  public isIncomingCall: boolean;

  @observable
  public isCallMuted: boolean;

  @observable
  public isCallOnHold = false;

  @observable
  public isCallCanceled = false;

  @observable
  public remoteAudio: HTMLAudioElement;

  @observable
  public loggedInPersonId: number;

  public phoneStore: PhoneStore;

  @observable
  keypadNumber = [];

  @observable
  public session: Session;

  @observable
  public ringtone: HTMLAudioElement;

  @observable
  public startOfCall: string = null;

  @observable
  public pickUpTime: string = null;

  @observable
  public incomingTone: HTMLAudioElement;

  @observable
  public secondIncomingTone: HTMLAudioElement;

  @observable
  public lostConnectionTone: HTMLAudioElement;

  @observable
  public localCallDisconnectedTone: HTMLAudioElement;

  @observable
  public keyPressTone0: HTMLAudioElement;

  @observable
  public keyPressTone1: HTMLAudioElement;

  @observable
  public keyPressTone2: HTMLAudioElement;

  @observable
  public keyPressTone3: HTMLAudioElement;

  @observable
  public keyPressTone4: HTMLAudioElement;

  @observable
  public keyPressTone5: HTMLAudioElement;

  @observable
  public keyPressTone6: HTMLAudioElement;

  @observable
  public keyPressTone7: HTMLAudioElement;

  @observable
  public keyPressTone8: HTMLAudioElement;

  @observable
  public keyPressTone9: HTMLAudioElement;

  @observable
  public keyPressTonePound: HTMLAudioElement;

  @observable
  public keyPressToneStar: HTMLAudioElement;

  @observable
  public person: IPromiseBasedObservable<ContactModelV2> = null;

  @observable
  public contact: ContactModelV2 = null;

  @observable
  public otherContacts: ContactModelV2[] = null;

  @observable
  public callerIdName: string;

  @observable
  public sipFromUri: string = null;

  @observable
  public sipToUri: string = null;

  @action
  public setCallerIdName = (callerIdName: string) => {
    this.callerIdName = callerIdName;
  };

  @action
  public setContact = (contact: ContactModelV2) => {
    if (!isNullOrUndefined(contact)) {
      this.contact = contact;
    }
  };

  @action
  public setOtherContacts = (otherContacts: ContactModelV2[]) => {
    if (!isNullOrUndefined(otherContacts) && otherContacts.length) {
      this.otherContacts = otherContacts;
    }
  };

  @action
  public setPerson = (person: IPromiseBasedObservable<ContactModelV2>) => {
    if (!isNullOrUndefined(person)) {
      this.person = fromPromise(person);
    }
  };

  @action
  setIncomingCallValue = (call: boolean) => (this.isIncomingCall = call);

  @action
  setLoggedInUserCallerId = (loggedInUserCallerId: string) =>
    (this.currentUserCallerId = loggedInUserCallerId);

  @action
  setLoggedInAccountId = (loggedInAccountId: number) =>
    (this.loggedInUserAccountId = loggedInAccountId);

  @action
  setLoggedInPersonId = (loggedInPersonId: number) =>
    (this.loggedInPersonId = loggedInPersonId);

  @action
  confirmExit(e) {
    const confirmationMessage =
      'You are currently on a call, exiting will disconnect the call. Do you wish to continue?';
    (e || window.event).returnValue = confirmationMessage;
    return confirmationMessage;
  }

  @action
  startTimer() {
    this.startTime = Date.now();
    this.elapsedTime = 0;
    this.updateTime();
    const interval = setInterval(() => {
      if (this.isCallConnected) {
        this.updateTime();
      } else {
        clearInterval(interval);
      }
    }, 1000);
  }

  @action
  public dialPadNumDtmf(dialDigit: string) {
    const DTMF_VOLUME = 0.2;

    switch (dialDigit) {
      case '0':
        this.keyPressTone0.volume = DTMF_VOLUME;
        void this.keyPressTone0.play();
        break;
      case '1':
        this.keyPressTone1.volume = DTMF_VOLUME;
        void this.keyPressTone1.play();
        break;
      case '2':
        this.keyPressTone2.volume = DTMF_VOLUME;
        void this.keyPressTone2.play();
        break;
      case '3':
        this.keyPressTone3.volume = DTMF_VOLUME;
        void this.keyPressTone3.play();
        break;
      case '4':
        this.keyPressTone4.volume = DTMF_VOLUME;
        void this.keyPressTone4.play();
        break;
      case '5':
        this.keyPressTone5.volume = DTMF_VOLUME;
        void this.keyPressTone5.play();
        break;
      case '6':
        this.keyPressTone6.volume = DTMF_VOLUME;
        void this.keyPressTone6.play();
        break;
      case '7':
        this.keyPressTone7.volume = DTMF_VOLUME;
        void this.keyPressTone7.play();
        break;
      case '8':
        this.keyPressTone8.volume = DTMF_VOLUME;
        void this.keyPressTone8.play();
        break;
      case '9':
        this.keyPressTone9.volume = DTMF_VOLUME;
        void this.keyPressTone9.play();
        break;
      case '*':
        this.keyPressToneStar.volume = DTMF_VOLUME;
        void this.keyPressToneStar.play();
        break;
      case '#':
        this.keyPressTonePound.volume = DTMF_VOLUME;
        void this.keyPressTonePound.play();
        break;
    }
    this.sendDtmf(dialDigit);
  }

  sendDtmf = (dialDigit: string) =>
    this.session.sessionDescriptionHandler.sendDtmf(dialDigit);

  @action
  updateTime() {
    const time = Date.now() - this.startTime;
    this.startTime = Date.now();
    this.elapsedTime += time;
    let tempTime = this.elapsedTime;
    tempTime = Math.floor(tempTime / 1000);
    const seconds = tempTime % 60;
    tempTime = Math.floor(tempTime / 60);
    const minutes = tempTime % 60;
    tempTime = Math.floor(tempTime / 60);
    const hours = tempTime % 60;
    const hourString = hours <= 9 ? '0' + hours : hours.toString();
    const minutesString = minutes <= 9 ? '0' + minutes : minutes.toString();
    const secondsString = seconds <= 9 ? '0' + seconds : seconds.toString();
    this.timerDisplay =
      hourString === '00'
        ? minutesString + ' : ' + secondsString
        : hourString + ' : ' + minutesString + ' : ' + secondsString;
  }

  @action
  resetDisplay() {
    this.timerDisplay = '00:00';
    this.startTime = 0;
    this.elapsedTime = 0;
    this.startOfCall = null;
    this.pickUpTime = null;
  }

  @action
  setIsCallConnected(call: boolean) {
    this.isCallConnected = call;
    const momentInst = moment().tz(TIMEZONE_IDENTIFIER).utc();
    if (call) {
      this.pickUpTime = momentInst.format().toString();
      if (window.addEventListener) {
        (window as any).addEventListener('beforeunload', this.confirmExit);
      }
    } else {
      if ((window as any).removeEventListener) {
        (window as any).removeEventListener('beforeunload', this.confirmExit);
      }
    }
  }

  @action
  public reject() {
    void this.pauseAndResetPlayPromise(this.ringtone, this.ringtonePlayPromise);

    this.isUserInitiatedTermination = true;
    this.terminationContext.reason = TerminationReason.REMOTE_BYE;
    this.terminationContext.statusCode = 603;
    this.terminationContext.reasonPhrase = 'Decline';
    this.terminationContext.sipjsCause = 'REJECT';

    this.isCallCanceled = true;
    void (this.session as Invitation).reject({ statusCode: 603 });
  }

  @action
  public cancel() {
    void this.pauseAndResetPlayPromise(this.ringtone, this.ringtonePlayPromise);

    if (
      this.phoneStore.isWebRTCConnected &&
      !this.phoneStore.isWebRTCReconnecting &&
      !this.phoneStore.isCallTimeout
    ) {
      this.isUserInitiatedTermination = true;
      this.terminationContext.reason = TerminationReason.CANCEL;
    }

    if (this.isCallConnecting) {
      this.clearKeyPress();
      this.setShowDialPad(false);
      this.muteIconState('microphone');
      this.setIsCallMuted(false);
      this.onHoldIconState('pause circle outline');
      this.setIsCallOnHold(false);
      this.setIsMediaConnecting(false);
      this.setIsCallConnected(false);
      this.setIsCallConnecting(false);
      this.setIsMediaConnected(false);
      void this.pauseAndResetPlayPromise(
        this.ringtone,
        this.ringtonePlayPromise
      );
      this.clearPersonAndContact();
      this.resetDisplay();
    }
    if (this.session instanceof Inviter) {
      this.isCallCanceled = true;
      void this.session.cancel();
    }
    this.closeCurrentStreams();
  }

  @action
  public networkTermination() {
    void this.pauseAndResetPlayPromise(this.ringtone, this.ringtonePlayPromise);

    this.terminationContext.reason = TerminationReason.NETWORK_TIMEOUT;
    this.terminationContext.statusCode = 408;
    this.terminationContext.reasonPhrase = 'Network Timeout';
    this.terminationContext.sipjsCause = 'NETWORK_ERROR';

    if (this.isCallConnecting || this.isCallConnected) {
      this.clearKeyPress();
      this.setShowDialPad(false);
      this.muteIconState('microphone');
      this.setIsCallMuted(false);
      this.onHoldIconState('pause circle outline');
      this.setIsCallOnHold(false);
      this.setIsMediaConnecting(false);
      this.setIsCallConnected(false);
      this.setIsCallConnecting(false);
      this.setIsMediaConnected(false);
      void this.pauseAndResetPlayPromise(
        this.ringtone,
        this.ringtonePlayPromise
      );
      this.clearPersonAndContact();
      this.resetDisplay();
    }

    if (this.session) {
      if (this.session instanceof Inviter) {
        this.isCallCanceled = true;
        void this.session.cancel();
      } else {
        void this.session.bye();
      }
    }

    this.closeCurrentStreams();
  }

  /**
   * When the Client recieve a Call they can Pickup.
   * We setup Early media `options: ServerContextAcceptOptions`, but not render it until the call is accepted
   * Sesion has Event called `accept(options?: ServerContextAcceptOptions): Session` this is when the audio gets rendered;
   */
  @action
  public pickup = async () => {
    // Check if new softphone is enabled - if so, don't execute legacy softphone logic
    const loggedInPersonId = this.rootStore.personStore.loggedInPersonId;
    if (loggedInPersonId) {
      const coordinator = SoftphoneCoordinator.getInstance();
      const isNewSoftphoneEnabled = coordinator.readPreferenceSync(loggedInPersonId);
      if (isNewSoftphoneEnabled) {
        console.log('[PhoneCallModel] New softphone is enabled, skipping legacy pickup');
        return;
      }
    }

    await this.phoneStore.refreshMicrophonePermissionStatus('incoming');
    if (
      this.phoneStore.browserMicrophonePermissionState === 'denied' ||
      !this.phoneStore.incomingPhoneCalls?.length
    ) {
      return;
    }

    // Record answer time for inbound calls
    this.callAnswerTime = Date.now();

    void this.pauseAndResetPlayPromise(
      this.incomingTone,
      this.incomingTonePlayPromise
    );
    const options: InvitationAcceptOptions = {
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: {
            audio: {
              deviceId: this.phoneStore.microphoneId
                ? { exact: this.phoneStore.microphoneId }
                : 'default',
            },
          },
          video: false,
        },
      },
      sessionDescriptionHandlerModifiers:
        this.getSessionDescriptionHandlerModifiers(),
    };

    if (this.session === undefined) {
      return;
    }
    const activeConference =
      !isNullOrUndefined(this.rootStore.conversationStore) &&
      this.rootStore.uiStore.IsOnVideoConference;
    if (activeConference) {
      void localforage.setItem<string>(ACTIVE_CALL, this.session.id);
    }
    this.setIsCallConnected(true);
    this.setIncomingCall(true);
    const index = this.phoneStore.incomingPhoneCalls.findIndex(
      (item) => item.session.id === this.session.id
    );
    this.phoneStore.incomingPhoneCalls.splice(index, 1);
    this.phoneStore.setPhoneCalls(this);
    this.phoneStore.setOtherSessionsOnHold(this.session);
    void (this.session as Invitation).accept(options);
    this.phoneStore.configureMicrophone(this.phoneStore.microphoneId);
    this.phoneStore.configureSpeaker(this.phoneStore.speakerId);
    this.phoneStore.configureRingTone(this.phoneStore.ringtoneSpeakerId);

    return;
  };

  handleOnAccept() {
    this.setIsCallConnecting(false);
    this.setIsCallConnected(true);
    this.setIsMediaConnected(true);
    this.startTimer();
    void this.pauseAndResetPlayPromise(this.ringtone, this.ringtonePlayPromise);
    void this.pauseAndResetPlayPromise(
      this.incomingTone,
      this.incomingTonePlayPromise
    );
    void this.pauseAndResetPlayPromise(
      this.secondIncomingTone,
      this.secondIncomingTonePlayPromise
    );
    this.setupLocalMedia();
    this.setupRemoteMedia();
    const activeConference =
      !isNullOrUndefined(this.rootStore.conversationStore) &&
      this.rootStore.uiStore.IsOnVideoConference;
    if (activeConference) {
      void localforage.setItem<string>(ACTIVE_CALL, this.session.id);
    }
  }

  isCallMissed = () =>
    this.isIncomingCall && !this.isCallCanceled && !this.pickUpTime;

  /**
   * Target address should be string to look like  var target = `test2@example.onsip.com`;
   * Making a Blind Transfer session.refer(target)
   */
  @action
  blindTransfer = (phoneNumber: string | number) => {
    pushToGTMDataLayer('transferCall', {
      callTransferType: 'Blind',
    });
    let personIdURI: string = null;
    personIdURI = phoneNumber.toString();
    if (this.session && this.session.state === 'Established') {
      if (
        this.rootStore.configStore.signedInPersonConfig.state === 'fulfilled'
      ) {
        const removeAllButNumbers = ['pr', 'room'].some((strings) =>
          personIdURI.includes(strings)
        )
          ? personIdURI
          : personIdURI.replace(/[^0-9+*]/g, '');
        const url =
          'sip:' +
          removeAllButNumbers +
          '@' +
          this.rootStore.configStore.signedInPersonConfig.value.data.webRtc
            .domain;
        const uri = UserAgent.makeURI(url.replace(/\s/g, ''));
        void this.session.refer(uri);
        pushToGTMDataLayer('transferCall', {
          callTransferType: 'Blind',
        });
      } else {
        logError(
          null,
          'The Domain address is not accessible, user not logged in or pending state!',
          'PhoneCallModel',
          'blindTransfer'
        );
      }
    } else {
      logError(
        null,
        'The Call is not Active right now, blind transfer did nothing!',
        'PhoneCallModel',
        'blindTransfer'
      );
    }
  };

  /**
   * Target straight to VoiceMail `Sip:*`
   */

  @action
  forwardToVoiceMail = (extension: string | number) => {
    let personIdURI: string = null;
    personIdURI = extension.toString();
    if (this.rootStore.configStore.signedInPersonConfig.state === 'fulfilled') {
      pushToGTMDataLayer('transferCall', {
        callTransferType: 'VoiceMail',
      });
      const url =
        'sip:*' +
        personIdURI +
        '@' +
        this.rootStore.configStore.signedInPersonConfig.value.data.webRtc
          .domain;
      const uri = UserAgent.makeURI(url.replace(/\s/g, ''));
      void this.session.refer(uri);
    }
  };

  /**
   * initiates the call to the third Party for WarmTransfer if the client accepts it.
   * @param phoneNumber is the target Number
   */
  @action
  warmTransfer(phoneNumber: string | number) {
    this.phoneStore.setWarmTransferOld(this);
    pushToGTMDataLayer('transferCall', {
      callTransferType: 'Warm',
    });
    if (isString(phoneNumber) && phoneNumber.indexOf('pr') === 0) {
      void this.phoneStore.callWithPerson(phoneNumber.substring(2), null, true);
    } else {
      void this.phoneStore.callWithPerson(null, phoneNumber.toString(), true);
    }
  }

  @action
  forwardWarmTransfer() {
    void this.session.refer(this.phoneStore.warmTransferOld.session);
  }

  /**
   * Formatting the Entered PhoneNumber to what we need. We are using PhoneUtil Library
   */
  @computed
  get FormattedNumber() {
    // short Code Support
    if (!this.phoneNumber) {
      return;
    } else {
      const phoneNumber = this.phoneNumber.replace(/^00/, '+');

      if (
        (phoneNumber.length >= 3 && phoneNumber.length <= 5) ||
        phoneNumber.startsWith('011')
      ) {
        return phoneNumber;
      }
      try {
        if (phoneUtil.isPossibleNumberString(phoneNumber, 'US')) {
          const parsePhoneNumber = phoneUtil.parse(phoneNumber, 'US');
          return phoneUtil.format(parsePhoneNumber, PhoneNumberFormat.NATIONAL);
        } else {
          console.error('Invalid Phone Number', phoneNumber);
          return '';
        }
      } catch (err) {
        logError(
          err,
          'Invalid phone number, failed on isPossibleNumberString',
          'PhoneCallModel',
          'FormattedNumber'
        );
      }
    }
  }

  /**
   * Just before the call connects we set it up
   */
  @action
  setIsCallConnecting(call: boolean) {
    this.isCallConnecting = call;
  }

  @action
  setIsMediaConnecting(call: boolean) {
    this.isMediaConnecting = call;
  }

  @action
  setIsMediaConnected(call: boolean) {
    this.isMediaConnected = call;
  }

  @observable
  public keysPressed = '';

  @action
  public clearKeyPress = () => (this.keysPressed = '');

  @observable
  public muteIcon = 'microphone';

  @observable
  public onHoldIcon = 'pause circle outline';

  @observable
  public callQueue = observable.map<PhoneCallModel>();

  public localValueForIncoming: boolean;

  @observable
  public transferMode = false;

  @action
  setTransferMode = (transferMode: boolean) => {
    console.debug('setTransferMode', transferMode);
    this.transferMode = transferMode;
  };

  @action
  public setIsCallMuted = (isCallMuted: boolean) =>
    (this.isCallMuted = isCallMuted);

  @action
  public setIsCallOnHold = (isCallOnHold: boolean) =>
    (this.isCallOnHold = isCallOnHold);

  @action
  public keysPress = (keyPress: string) =>
    (this.keysPressed = this.keysPressed + keyPress);

  @action
  public muteIconState = (muteIcon: string) => (this.muteIcon = muteIcon);

  @action
  public onHoldIconState = (onHoldIcon: string) =>
    (this.onHoldIcon = onHoldIcon);

  @action
  setShowDialPad = (showDialPad: boolean) => (this.showDialPad = showDialPad);

  @observable
  public isWarmTransfer = false;

  @action
  public setWarmTransfer = (isWarmTransfer: boolean) =>
    (this.isWarmTransfer = isWarmTransfer);

  @observable
  public isMerge = false;

  @action
  public setMergeTransfer = (isMerge: boolean) => (this.isMerge = isMerge);

  @action
  public setConferenceId = (conferenceId: string) =>
    (this.conferenceId = conferenceId);

  @action
  setIncomingCall(call: boolean) {
    this.isIncomingCall = call;
    this.localValueForIncoming = call;
  }

  @action
  public toggleDialPad = () => {
    this.setShowDialPad(!this.showDialPad);
  };

  @observable
  onHoldSession: Session = null;

  @action
  setHoldSession = (onHoldSession: Session) =>
    (this.onHoldSession = onHoldSession);

  @observable
  public mediaStream: MediaConstraints;

  @action
  setPhoneNumber(num: string) {
    this.phoneNumber = num;
  }

  @action
  setPhoneURI(num: URI) {
    this.callUri = num?.toString();
  }
  /**
   * Session needs to be Muted
   */

  @action
  public mute() {
    this.isCallMuted = true;
    //@ts-ignore
    const pc = this.session.sessionDescriptionHandler.peerConnection;
    const togglemute = this.isCallMuted;
    if (pc.getSenders) {
      pc.getSenders().forEach(function (sender) {
        if (sender.track) {
          // track.muted
          sender.track.enabled = !togglemute;
        }
      });
    } else {
      pc.getReceivers().forEach(function (receiver) {
        if (receiver.track) {
          receiver.track.enabled = !togglemute;
        }
      });
    }
    pushToGTMDataLayer('callMute');
  }

  /**
   * Session needs to be unMuted
   */
  @action
  public unMute() {
    this.isCallMuted = false;
    //@ts-ignore
    const pc = this.session.sessionDescriptionHandler.peerConnection;
    const togglemute = this.isCallMuted;
    if (pc.getSenders) {
      pc.getSenders().forEach(function (sender) {
        if (sender.track) {
          sender.track.enabled = !togglemute;
        }
      });
    } else {
      pc.getReceivers().forEach(function (receiver) {
        if (receiver.track) {
          receiver.track.enabled = !togglemute;
        }
      });
    }
    void localforage.getItem<string>(SELECTED_MICROPHONE).then((mc) => {
      mc && this.phoneStore.configureMicrophone(mc);
    });
    void localforage.getItem<string>(SELECTED_SPEAKER).then((sp) => {
      sp && this.phoneStore.configureSpeaker(sp);
    });
  }

  /**
   * Holds the call.
   *
   * Session needs to be hold.
   *
   * @async
   * @public
   */
  @action
  public async hold() {
    this.isCallOnHold = true;
    void localforage.removeItem(ACTIVE_CALL);
    pushToGTMDataLayer('holdCall');
    return await this.session.invite({
      sessionDescriptionHandlerModifiers: [Web.holdModifier],
    });
  }

  /**
   * UnHolds the call.
   *
   * Session needs to be unHold.
   *
   * @async
   * @public
   */
  @action
  public unHold() {
    this.isCallOnHold = false;
    const activeConference =
      !isNullOrUndefined(this.rootStore.conversationStore) &&
      this.rootStore.uiStore.IsOnVideoConference;
    if (activeConference) {
      void localforage.setItem<string>(ACTIVE_CALL, this.session.id);
    }
    this.setupRemoteMedia();
    return this.session.invite({
      sessionDescriptionHandlerModifiers:
        this.getSessionDescriptionHandlerModifiers(),
    });
  }

  /**
   * The session needs to be terminated use the hangup
   */
  @action
  public hangUp() {
    void this.pauseAndResetPlayPromise(this.ringtone, this.ringtonePlayPromise);

    this.isUserInitiatedTermination = true;
    this.terminationContext.reason = TerminationReason.USER_HANGUP;
    this.terminationContext.statusCode = 200;
    this.terminationContext.reasonPhrase = 'OK';
    this.terminationContext.sipjsCause = 'BYE';

    void this.session.bye();
    this.closeCurrentStreams();
  }

  public mediaConstraints: any = {
    audio: true,
    video: false,
  };

  @action
  clearPersonAndContact = () => {
    this.person = null;
    this.contact = null;
  };

  /**
   * setting up remote audio for the client side
   */
  setupLocalMedia = () => {
    //@ts-ignore
    const pc = this.session.sessionDescriptionHandler.peerConnection;
    const localStream = new MediaStream();
    if (this.remoteAudio) {
      pc.getSenders().forEach((sender) => {
        const track = sender.track;
        if (track && track.kind === 'audio') {
          localStream.addTrack(track);
        }
      });

      void this.pauseAndResetPlayPromise(
        this.remoteAudio,
        this.localMediaPlayPromise,
        localStream
      ).then((wp) => {
        const HOLD_AUDIO_VOLUME = 0.49;
        this.remoteAudio.volume = HOLD_AUDIO_VOLUME;
        this.localMediaPlayPromise = this.remoteAudio.play();
      });
    }
    //@ts-ignore
    window.localStream2 = localStream;
  };

  /**
   * setting up remote audio for the destination
   */
  setupRemoteMedia = () => {
    //@ts-ignore
    const pc = this.session.sessionDescriptionHandler.peerConnection;
    let remoteStream = new MediaStream();
    if (pc.getReceivers) {
      pc.getReceivers().map((reciever) => {
        const track = reciever.track;
        if (track) {
          remoteStream.addTrack(track);
        } else {
          remoteStream = (pc as any).getRemoteStreams()[0];
        }
      });
      void this.pauseAndResetPlayPromise(
        this.remoteAudio,
        this.remoteMediaPlayPromise,
        remoteStream
      ).then((wp) => {
        this.remoteMediaPlayPromise = this.remoteAudio.play();
      });
    } else {
      console.warn('Reciver doesnt have an Audio');
    }
    //@ts-ignore
    window.remoteStream = remoteStream;
  };

  @action
  public setSipFromUri = (uri: string) => {
    this.sipFromUri = uri;
  };

  @action
  public setSipToUri = (uri: string) => {
    this.sipToUri = uri;
  };

  /**
   * Determines whether to apply PCMU enforcement modifier based on call type and feature flag
   * @returns Array with modifiers to be applied
   */
  /**
   * Determines whether to apply PCMU enforcement modifier based on call type and direction
   * @returns Array with modifiers to be applied
   */
  private getSessionDescriptionHandlerModifiers(): SessionDescriptionHandlerModifier[] {
    const sdpService = SdpService.getInstance();

    const isCallExternal = sdpService.analyzeIfCallIsExternal(
      this.callUri,
      this.phoneNumber,
      this.sipFromUri,
      this.sipToUri
    );
    const isFeatureFlagEnabled = this.rootStore.personStore.ldClient.variation(
      COMMUNICATOR_ENFORCE_PCMU_CODEC,
      false
    ) as boolean;

    if (isCallExternal && isFeatureFlagEnabled) {
      bugsnagClient.notify(
        'VoipMonitorService: PCMU enforcement modifier applied for external outbound call',
        (event) => {
          event.severity = 'info';
          event.context = 'PhoneCallModel';
          event.addMetadata('callContext', {
            loggedInPersonId: this.loggedInPersonId,
            callUri: this.callUri,
            phoneNumber: this.phoneNumber,
            isIncomingCall: this.isIncomingCall,
          });
        }
      );

      const pcmuEnforcementModifier = (description: RTCSessionDescriptionInit) => sdpService.pcmuEnforcementModifier(description)

      return [pcmuEnforcementModifier];
    } else {
      return [];
    }
  }
}

export default PhoneCallModel;
